import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Check, X, MapPin } from "lucide-react";

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => api.get("/locations").then((r) => setLocations(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/locations", { name: name.trim() });
      setName("");
      toast.success("Location added");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const saveRename = async (id) => {
    try {
      await api.put(`/locations/${id}`, { name: editName.trim() });
      toast.success("Location renamed");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/locations/${deleteId}`);
      toast.success("Location deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setDeleteId(null);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">
          Administration
        </div>
        <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
          Location Management
        </h1>
        <p className="text-zinc-500 mt-1 text-sm">
          Manage the sites available for jobs and user assignments.
        </p>
      </div>

      <form onSubmit={add} className="flex gap-2 mb-6">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="location-name-input"
          placeholder="New location name (e.g. East Dock)"
          className="flex-1 px-4 py-2.5 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <button
          type="submit"
          disabled={adding || !name.trim()}
          data-testid="add-location-submit"
          className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2 disabled:opacity-50"
        >
          <Plus size={18} /> Add
        </button>
      </form>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] divide-y divide-zinc-100">
        {locations.length === 0 ? (
          <div className="px-4 py-10 text-center text-zinc-400 text-sm">No locations yet.</div>
        ) : (
          locations.map((l) => (
            <div
              key={l.id}
              data-testid={`location-row-${l.id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
            >
              {editId === l.id ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    data-testid={`location-edit-input-${l.id}`}
                    className="flex-1 px-3 py-1.5 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  <button
                    onClick={() => saveRename(l.id)}
                    data-testid={`location-save-${l.id}`}
                    className="text-emerald-600 hover:text-emerald-800"
                  >
                    <Check size={18} />
                  </button>
                  <button onClick={() => setEditId(null)} className="text-zinc-400 hover:text-zinc-700">
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <span className="flex items-center gap-2 font-semibold text-zinc-900">
                    <MapPin size={16} className="text-brand" /> {l.name}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        setEditId(l.id);
                        setEditName(l.name);
                      }}
                      data-testid={`edit-location-${l.id}`}
                      className="text-zinc-400 hover:text-brand transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteId(l.id)}
                      data-testid={`delete-location-${l.id}`}
                      className="text-zinc-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this location?</AlertDialogTitle>
            <AlertDialogDescription>
              Locations that are still used by jobs cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-location"
              className="bg-red-600 hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
